import React from 'react';
import { View } from '@react-pdf/renderer';
import { StandardPdfTemplate } from '../../templates/StandardPdfTemplate';
import { ProjectData, ProjectInfoData } from '../../shared/types/PdfCommonTypes';
import { TFunction } from 'i18next';
import { IGeogridData } from '../../../../api/IApiResponse';
import { ContainerYardResultsCore } from './sections/ContainerYardResultsCore';
import { ContainerYardGeoGridCore } from './sections/ContainerYardGeoGridCore';

interface CombinedResultsPdfDocumentProps {
    projectInfo: any;
    dynamicProject: any;
    output: any;
    tireImageUnreinforced?: string;
    tireImageReinforced?: string;
    chartImage?: string;
    geoGridData?: IGeogridData;
    geogridImage?: string;
    t: TFunction;
}

const CombinedResultsPdfDocumentV2: React.FC<CombinedResultsPdfDocumentProps> = ({
    projectInfo,
    dynamicProject,
    output,
    tireImageUnreinforced,
    tireImageReinforced,
    chartImage,
    geoGridData,
    geogridImage,
    t,
}) => {
    const projectData = dynamicProject?.data;
    const projectType = projectData?.type || 'container_yard';
    const unitSystem = projectData?.unit || 'Metric';

    // Prepare data for StandardPdfTemplate
    const projectDataForTemplate: ProjectData = {
        projectName: projectData?.projectName || 'N/A',
        projectType: projectType,
        unit: unitSystem,
        mapImage: projectData?.mapImage,
    };

    const projectInfoDataForTemplate: ProjectInfoData = {
        data: {
            nrProject: projectInfo?.nrProject || projectData?.projectInfo?.data?.nrProject || 'N/A',
        },
        projectOwner: projectInfo?.projectOwner || projectData?.projectInfo?.projectOwner || 'N/A',
        designerFirmName: projectInfo?.designerFirmName || projectData?.projectInfo?.designerFirmName || 'N/A',
        designerFirmCountry: projectInfo?.designerFirmCountry || projectData?.projectInfo?.designerFirmCountry || 'N/A',
        designerFirmCity: projectInfo?.designerFirmCity || projectData?.projectInfo?.data?.designerFirmCity || 'N/A',
        coordinates: projectInfo?.coordinates || projectData?.projectInfo?.coordinates || [null, null],
    };

    // Get project type display name
    const getProjectTypeDisplayName = () => {
        switch (projectType) {
            case 'container_yard':
                return t('container_yard') || 'Container Yard';
            case 'parking_deck':
                return t('parking_deck') || 'Parking Deck';
            default:
                return t('project') || 'Project';
        }
    };

    return (
        <StandardPdfTemplate
            projectType={projectType}
            projectData={projectDataForTemplate}
            projectInfoData={projectInfoDataForTemplate}
            t={t}
            headerConfig={{
                customTitle: t('pdf_header_technical_report_groundmac') || 'GROUNDMAC - Design Technical Report',
                customProjectType: getProjectTypeDisplayName(),
                showGeogridSubtitle: true,
            }}
            footerConfig={{
                customText: t('pdf_footer') || 'MACread eDesign',
                customNotice: t('pdf_footer_notice') || 'Footer notice',
            }}
            showProjectInfo={true}>
            {/* PART 1: CONTAINER YARD SECTION */}
            <ContainerYardResultsCore
                output={output}
                projectData={projectData}
                tireImageUnreinforced={tireImageUnreinforced}
                tireImageReinforced={tireImageReinforced}
                chartImage={chartImage}
                t={t}
                showInputData={true}
                showChart={true}
                showResults={true}
            />

            {/* PART 2: GEOGRID SECTION */}
            {geoGridData && (
                <>
                    <View break />
                    <ContainerYardGeoGridCore
                        geoGridData={geoGridData}
                        geogridImage={geogridImage}
                        projectData={projectData}
                        t={t}
                        showInputData={true}
                        showGeoGridImage={true}
                        showCourseResults={true}
                        customTitle="MACREAD - GEOGRID STUDIO"
                        isCombinedPdf={true}
                    />
                </>
            )}
        </StandardPdfTemplate>
    );
};

export default CombinedResultsPdfDocumentV2;
